'use client';

import { useListItem, useMergeRefs } from '@floating-ui/react';
import {
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { MenuItemContext } from './context/menu-item.context';
import { MenuRootContext } from './context/menu-root.context';
import { menuItemRecipe } from './styles/menu-item.styles';
import type { MenuItemPropsInterface } from './types/menu-item.types';

/**
 * @since 10.0.0
 */
export const MenuItem = forwardRef<HTMLLIElement, MenuItemPropsInterface>(
  (props: MenuItemPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      css: cssProp,
      className,
      disabled = false,
      onClick,
      ...rest
    } = props;

    const { menu } = useContext(MenuRootContext);
    const localRef = useRef<HTMLElement>(null);
    const { ref: itemRef, index: _index } = useListItem();
    const mergedRefs = useMergeRefs([itemRef, localRef, forwardedRef]);

    const styles = css(menuItemRecipe.raw({ disabled }), cssProp);

    const Component = asChild ? Slot : 'button';

    const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        // Do not close menu if the Menu.Item itself is a trigger for another floating element
        const { current } = localRef;
        if (
          !current?.hasAttribute('aria-controls') &&
          !current?.hasAttribute('aria-expanded') &&
          !current?.hasAttribute('aria-haspopup')
        ) {
          menu.onClose?.(event as unknown as Parameters<typeof menu.onClose>[0]);
        }

        // onClick from ComponentProps<'li'> — cast the event since the underlying
        // interactive element is a <button> but the public API types as <li>
        onClick?.(event as unknown as React.MouseEvent<HTMLLIElement>);
      },
      [menu, onClick],
    );

    // Don't use the tabIndex provided by useListNavigation (CDS-5822)
    // Arrow key navigation still works via the onKeyDown handlers in getItemProps.
    const { tabIndex: _droppedTabIndex, ...itemProps } = menu.getItemProps({
      onClick: onClickHandler as React.MouseEventHandler,
      ...rest,
    });

    const contextValue = useMemo(() => ({ anchor: asChild, disabled }), [asChild, disabled]);

    return (
      <MenuItemContext.Provider value={contextValue}>
        <li
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Menu.ListItem"
          className="c11n_menu__list-item"
        >
          <Component
            ref={mergedRefs as unknown as React.Ref<HTMLButtonElement>}
            data-c11n-version={__C11N_VERSION__}
            data-c11n-component="Menu.Item"
            type={asChild ? undefined : 'button'}
            className={cx('c11n', 'c11n_menu__item', 'group', styles, className)}
            disabled={disabled}
            {...itemProps}
          >
            {children}
          </Component>
        </li>
      </MenuItemContext.Provider>
    );
  },
);

MenuItem.displayName = 'Menu.Item';
